import { Fragment, memo, useMemo } from "react";

import { type IconName, Icon } from "@components/Icon";
import { Text } from "@components/Text";
import { type WithBreakpoints, getSizeWithBreakpoints } from "@react/utilities";
import {
  ThemeMode,
  type ProgressBarThresholdItem,
} from "@root/tablescape/state";
import { type TTailwindString, tw } from "classnames";

type SizeValue = "sm" | "lg";

const styles = {
  container: ({
    border,
    hasIcon,
    size,
    theme,
    type,
  }: {
    border: boolean;
    hasIcon: boolean;
    size: WithBreakpoints<SizeValue>;
    theme?: ThemeMode;
    type: "cart" | "threshold" | "default" | "review";
  }) =>
    tw(
      "flex",
      "flex-1",
      "overflow-hidden",
      {
        [tw("h-1", "rounded")]: size.sm === "sm" && !hasIcon,
        [tw("h-1", "rounded-tl", "rounded-bl")]: size.sm === "sm" && hasIcon,
        [tw("h-2", "rounded-lg")]: size.sm === "lg" && !hasIcon,
        [tw("h-2", "rounded-tl-lg", "rounded-bl-lg")]:
          size.sm === "lg" && hasIcon,
      },
      {
        [tw("md:h-1", "md:rounded")]: size.md === "sm" && !hasIcon,
        [tw("md:h-1", "md:rounded-tl", "md:rounded-bl")]:
          size.md === "sm" && hasIcon,
        [tw("md:h-2", "md:rounded-lg")]: size.md === "lg" && !hasIcon,
        [tw("md:h-2", "md:rounded-tl-lg", "md:rounded-bl-lg")]:
          size.md === "lg" && hasIcon,
      },
      {
        [tw("outline" as TTailwindString)]: border,
        [tw("outline-1" as TTailwindString)]: border,
        "outline-black": border,
      },
      {
        [tw({
          [tw("bg-interactive-char-hover/10" as TTailwindString)]:
            theme !== ThemeMode.DARK,
          [tw("bg-background-primary-inverse")]: theme === ThemeMode.DARK,
        })]: type !== "threshold",
        "bg-white": type === "threshold",
      },
      {
        [tw("mr-8")]: hasIcon,
      }
    ),
  bar: tw(
    "flex",
    "w-full",
    "h-full",
    "transition-all",
    "duration-[2s]" as TTailwindString
  ),
  separator: tw("flex", "w-[1px]" as TTailwindString, "h-2", "bg-black"),
  text: tw("flex"),
  thresholdItem: tw(
    "flex",
    "flex-1",
    "relative",
    "top-2",
    "mt-2.5",
    "h-3",
    "items-center",
    "justify-center"
  ),
  thresholdList: (hasIcon: boolean) =>
    tw(
      "absolute",
      "top-0",
      "left-0",
      "flex",
      "flex-row",
      "items-start",
      "w-full",
      "list-none",
      "m-0",
      "p-0",
      {
        "pr-8": hasIcon,
      }
    ),
  thresholdText: (item: ProgressBarThresholdItem) =>
    tw(
      styles.text,
      "px-2",
      "py-0.5",
      "rounded",
      "transition-colors",
      "duration-200",
      {
        [tw("text-misc-bright-blue")]:
          item.hasReachedThreshold && !item.isCurrentThreshold,
        [tw(
          "bg-misc-bright-blue",
          "text-white",
          "font-semibold",
          "uppercase",
          "tracking-widest"
        )]:
          item.hasReachedThreshold &&
          (item.isPreviousThreshold || item.isCurrentThreshold),
      }
    ),
  type: {
    default: tw("bg-background-primary-inverse"),
    cart: tw("bg-misc-bright-blue"),
    review: tw("bg-brick-700"),
  },
  wrapper: ({
    hasIcon,
    hasThresholdItemText,
  }: {
    hasIcon: boolean;
    hasThresholdItemText: boolean;
  }) =>
    tw("relative", "flex", "flex-1", "w-full", {
      "h-8": hasThresholdItemText,
      "h-5": !hasThresholdItemText && hasIcon,
    }),
  iconWrapper: (isBarFull: boolean) =>
    tw(
      "absolute",
      "-top-3",
      "right-1",
      "w-8",
      "h-8",
      "rounded-full",
      "border",
      "border-solid",
      "border-interactive-char",
      "flex",
      "justify-center",
      "items-center",
      "box-border",
      "transition",
      "delay-200",
      "duration-[2s]" as TTailwindString,
      {
        [tw("bg-interactive-white")]: !isBarFull,
        [tw("bg-misc-yellow-orange")]: isBarFull,
      }
    ),
  icon: tw("w-4.5", "h-4.5"),
};

type Props = {
  border?: boolean;
  className?: TTailwindString;
  iconName?: IconName;
  progressPercentage: number;
  size?: SizeValue | WithBreakpoints<SizeValue>;
  theme?: ThemeMode;
} & (
  | {
      color?: undefined;
      thresholdItems?: undefined;
      type: "cart" | "default" | "review";
    }
  | {
      color: string;
      thresholdItems: ProgressBarThresholdItem[];
      type: "threshold";
    }
);

const defaultSizes = {
  xs: { sm: "sm", md: "lg" },
  sm: { sm: "sm", md: "lg" },
  md: { sm: "sm", md: "lg" },
  mg: { sm: "sm", md: "lg" },
  lg: { sm: "sm", md: "lg" },
  xl: { sm: "sm", md: "lg" },
} as const;

const ProgressBarBase = ({
  border = false,
  className,
  color,
  iconName,
  progressPercentage,
  size: originalSize,
  theme,
  thresholdItems = [],
  type = "default",
}: Props) => {
  const size = useMemo(() => {
    const resolvedSize =
      typeof originalSize === "string"
        ? { sm: originalSize, md: originalSize }
        : { ...defaultSizes.md, ...originalSize };

    return getSizeWithBreakpoints(resolvedSize);
  }, [originalSize]);

  const containerClass = tw(
    styles.container({
      border,
      size,
      theme,
      type,
      hasIcon: !!iconName,
    }),
    className
  );

  const barClass = tw(styles.bar, {
    [styles.type.default]: type === "default",
    [styles.type.cart]: type === "cart",
    [styles.type.review]: type === "review",
  });

  const memoizedBarStyle = useMemo(
    () => ({
      background: color,
      clipPath: `inset(0 ${100 - progressPercentage}% 0 0 round 0.5rem)`,
    }),
    [color, progressPercentage]
  );

  const hasThresholdItems = useMemo(
    () => thresholdItems?.length > 0,
    [thresholdItems?.length]
  );

  const hasThresholdItemText = useMemo(
    () => thresholdItems?.some((item) => item.text),
    [thresholdItems]
  );

  return (
    <div
      className={styles.wrapper({ hasThresholdItemText, hasIcon: !!iconName })}
      data-theme={theme}
    >
      <div className={containerClass}>
        <div className={barClass} style={memoizedBarStyle} />
      </div>
      {hasThresholdItems && (
        <ol className={styles.thresholdList(!!iconName)}>
          {thresholdItems.map((item, index) => (
            <Fragment key={`${item}-${index}`}>
              <li className={styles.thresholdItem}>
                {item.text && (
                  <Text
                    align="center"
                    as="body2"
                    className={styles.thresholdText(item)}
                    leading="none"
                  >
                    {item.text}
                  </Text>
                )}
              </li>
              {index < thresholdItems.length - 1 && (
                <li
                  aria-hidden="true"
                  className={styles.separator}
                  key={`separator-${index}`}
                  role="presentation"
                />
              )}
            </Fragment>
          ))}
        </ol>
      )}
      {iconName && (
        <div className={styles.iconWrapper(progressPercentage === 100)}>
          <Icon className={styles.icon} name={iconName} />
        </div>
      )}
    </div>
  );
};

export const ProgressBar = memo(ProgressBarBase);
